import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { getAudioPublicToken, getPublicSongByToken, getSongById, } from "@/shared/api";
import { Loader } from "@/shared/ui";
import { IconX } from "@tabler/icons-react";
import { useAuth } from "@/app/providers/AuthProvider";
import { useLanguage } from "@/app/providers/LanguageProvider";
import { SongHub } from "@/pages/web/SongPublicPage/SongHub";
import { cn } from "@/shared/lib/utils";
import styles from "./SongDetailPanel.module.scss";
/**
 * Компактная панель деталей песни для «Моей музыки». Тонкая обёртка над `SongHub`
 * (variant="panel"). Панель всегда показывает СОБСТВЕННУЮ песню владельца, поэтому
 * данные берём напрямую с бэка:
 *  - если известен `songId` — ОДНИМ запросом `getSongById` (owner/authed), без
 *    лишнего резолва публичного токена. Найденный вариант (`audioId`) маппим в
 *    форму `PublicSong` для `SongHub`; скачивания идут по прямым presigned-URL из
 *    payload (токен не нужен);
 *  - фолбэк для входов без `songId` — прежний token-флоу
 *    (`getAudioPublicToken` → `getPublicSongByToken`).
 * Воспроизведение аудио идёт через ГЛОБАЛЬНЫЙ нижний плеер (у панели своего плеера
 * нет), а старт видео/караоке внутри `SongHub` глушит этот глобальный плеер.
 * Используется в двух местах:
 *  - ПК split-view «Моей музыки» (правая колонка);
 *  - мобильный bottom-sheet, выезжающий из глобального плеера.
 */
export const SongDetailPanel = ({ songId, audioId, onClose, className }) => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [token, setToken] = React.useState(null);
    const [data, setData] = React.useState(null);
    const [status, setStatus] = React.useState("loading");
    const t = (ru, en) => (language === "ru" ? ru : en);
    // Owner-песня (getSongById) → форма PublicSong для SongHub.
    const mapOwnerSong = React.useCallback((song) => {
        const audio = song.audios.find((a) => a.id === audioId) ?? song.audios[0];
        if (!audio)
            return null;
        // Модель стемов-строк (батч 16): минус — ОТДЕЛЬНАЯ строка `audios`
        // (`type === "instrumental"`, `parent_audio_id` === родитель), а не поле
        // `instrumental_url`. Находим дочернюю строку-минус выбранного аудио.
        const minusRow = song.audios.find((a) => a.type === "instrumental" && a.parent_audio_id === audio.id);
        return {
            title: audio.title || song.title,
            audio_url: audio.url,
            cover_url: audio.cover_url,
            text: song.text,
            download_url: audio.url,
            duration: null,
            // Панель всегда открыта у владельца → включаем owner-действия.
            owner_user_id: user?.id,
            audio_id: audio.id,
            song_id: song.id,
            // Контракт действий строки — только вместе с `provider`. Старый бэк
            // (без `provider`) отдаёт список без новых ключей; хаб тогда собирает
            // действия по-прежнему, как для публичного ответа без этих полей.
            ...(audio.provider != null
                ? {
                    provider: audio.provider,
                    available_actions: audio.available_actions,
                }
                : {}),
            instrumental: minusRow?.url && minusRow.status !== "failed"
                ? {
                    url: minusRow.url,
                    status: minusRow.status ?? "ready",
                }
                : null,
            wav: audio.wav_url ? { url: audio.wav_url } : null,
            media_outputs: (audio.media_outputs ?? []).map((m) => ({
                id: m.id,
                kind: m.kind,
                status: m.status ?? "ready",
                url: m.url ?? null,
                created_at: m.created_at,
            })),
            is_show_in_charts: audio.is_show_in_charts,
            minus_in_charts: minusRow?.is_show_in_charts,
        };
    }, [audioId, user?.id]);
    // Единый загрузчик данных панели (переиспользуется для первичной загрузки и
    // для поллинга восстановления). Возвращает данные + токен (у owner-пути —
    // null, скачивания идут по прямым presigned-URL).
    const fetchData = React.useCallback(async () => {
        // Основной путь: панель = собственная песня владельца → грузим данные
        // напрямую одним запросом (без резолва публичного токена).
        if (songId != null) {
            const song = await getSongById(songId);
            const mapped = mapOwnerSong(song);
            if (!mapped)
                throw new Error("SongDetailPanel: no audio in song");
            return { data: mapped, token: null };
        }
        // Фолбэк (входы без songId): прежний путь через публичный токен.
        const tok = await getAudioPublicToken(audioId);
        const song = await getPublicSongByToken(tok);
        return { data: song, token: tok };
    }, [songId, audioId, mapOwnerSong]);
    React.useEffect(() => {
        let cancelled = false;
        setStatus("loading");
        setData(null);
        setToken(null);
        fetchData()
            .then(({ data: loaded, token: tok }) => {
            if (cancelled)
                return;
            setToken(tok);
            setData(loaded);
            setStatus("ok");
        })
            .catch((e) => {
            console.error("SongDetailPanel: failed to load song", e);
            if (!cancelled)
                setStatus("error");
        });
        return () => {
            cancelled = true;
        };
    }, [fetchData]);
    return (_jsxs("div", { className: cn(styles.root, className), children: [status === "loading" && (_jsxs("div", { className: styles.state, children: [_jsx("button", { type: "button", className: styles.stateClose, onClick: onClose, "aria-label": t("Закрыть", "Close"), children: _jsx(IconX, { size: 18 }) }), _jsx(Loader, { size: "lg" })] })), status === "error" && (_jsxs("div", { className: styles.state, children: [_jsx("button", { type: "button", className: styles.stateClose, onClick: onClose, "aria-label": t("Закрыть", "Close"), children: _jsx(IconX, { size: 18 }) }), _jsx("p", { className: styles.stateText, children: t("Не удалось загрузить песню.", "Failed to load the song.") })] })), status === "ok" && data && (_jsx(SongHub, { token: token ?? undefined, data: data, variant: "panel", onClose: onClose }))] }));
};
export default SongDetailPanel;
